/**
 * ATL-001415 — React Query hooks for AI Email Intake (/api/v1/email-intake):
 * IMAP/SMTP mailbox connections (password or OAuth2 sign-in), sender rules,
 * the review queue, and monitoring.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { CommitRequestBody, PreviewResponse } from '../pages/load-import/load-import.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MailboxAuthType = 'password' | 'oauth2';
export type MailboxStatus =
  | 'connected'
  | 'paused'
  | 'needs_reauth'
  | 'error'
  /** Saved from a sign-in Microsoft held for admin approval; not read yet. */
  | 'pending_approval';
export type MailboxMode = 'shadow' | 'assisted' | 'auto';
export type MailboxOutputType = 'load' | 'tender' | 'quote';
export type SenderScope = 'any' | 'rules_only';
export type ReprocessStage = 'classify' | 'extract' | 'validate';

export interface MailboxSettings {
  classifyThreshold: number;
  autoCreateThreshold: number;
  requireSenderAuth: boolean;
  maxMessagesPerSync: number;
  backfillDays: number;
  initialLoadStatusId: string | null;
  markProcessed: boolean;
  maxPagesPerEmail: number;
  trustedAuthservId: string | null;
}

export interface MailboxOAuthView {
  authorizeUrl: string | null;
  revokeUrl: string | null;
  tokenUrl: string;
  clientId: string;
  scope: string | null;
  hasClientSecret: boolean;
  hasRefreshToken: boolean;
}

export interface EmailMailbox {
  id: string;
  name: string;
  emailAddress: string;
  authType: MailboxAuthType;
  username: string;
  hasPassword: boolean;
  oauth2: MailboxOAuthView | null;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean;
  folder: string;
  status: MailboxStatus;
  mode: MailboxMode;
  moduleVariant: string;
  outputType: MailboxOutputType;
  senderScope: SenderScope;
  settings: MailboxSettings;
  throttledUntil: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  consecutiveFailures: number;
  createdAt: string;
}

export interface MailboxOAuthInput {
  authorizeUrl?: string | null;
  revokeUrl?: string | null;
  tokenUrl: string;
  clientId: string;
  clientSecret?: string | null;
  refreshToken?: string | null;
  scope?: string | null;
}

export interface MailboxInput {
  name: string;
  emailAddress: string;
  authType: MailboxAuthType;
  username: string;
  password?: string | null;
  oauth2?: MailboxOAuthInput | null;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpSecure?: boolean;
  folder: string;
  mode: MailboxMode;
  moduleVariant: string;
  outputType: MailboxOutputType;
  senderScope: SenderScope;
  settings?: Partial<MailboxSettings>;
}

export interface ConnectionTestResult {
  imap: { ok: boolean; code?: string; message?: string; messages?: number };
  smtp: { ok: boolean; skipped?: boolean; code?: string; message?: string };
}

export interface SenderRule {
  id: string;
  pattern: string;
  action: 'customer' | 'block';
  customerId: string | null;
  customerName: string | null;
  autoCreate: boolean;
}

export interface SenderRuleInput {
  pattern: string;
  action: 'customer' | 'block';
  customerId?: string | null;
  autoCreate?: boolean;
}

export type EmailView = 'review' | 'created' | 'ignored' | 'failed' | 'shadow' | 'all';

export interface CreatedRef {
  id: string;
  displayId: string | null;
}

/** A created tender or quote (both are Quote rows) and the module it lives in. */
export interface CreatedQuoteRef extends CreatedRef {
  kind: 'tender' | 'quote';
  module: string | null;
}

export interface InboundEmailSummary {
  id: string;
  mailboxId: string;
  mailboxAddress: string;
  mailboxMode: MailboxMode;
  fromAddress: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
  status: string;
  filterReason: string | null;
  failureReason: string | null;
  classification: string | null;
  classificationConfidence: number | null;
  attachmentCount: number;
  proposalCount: number;
  decisions: string[];
  createdLoads: CreatedRef[];
  createdQuotes: CreatedQuoteRef[];
  relatedLoadId: string | null;
  customerName: string | null;
  referenceNumber: string | null;
  issueCount: number;
}

export interface ProposalIssue {
  code: string;
  field?: string;
  detail?: string;
}

export interface ProposalConflict {
  field: string;
  chosen: string | null;
  chosenSource: string | null;
  values: Array<{ value: string | null; source: string }>;
}

export interface ProposalMatches {
  customerId: string | null;
  customerSource: 'sender_rule' | 'alias' | 'name' | null;
  stopLocationIds: Array<string | null>;
  /** How each stop was matched; 'address' = street + postal code, not the name. */
  stopMatchSources?: Array<'alias' | 'name' | 'address' | null>;
  customerCandidates?: Array<{ id: string; name: string }>;
  stopCandidates?: Array<Array<{ id: string; name: string }>>;
  equipmentTypeId?: string | null;
  commodityCategoryId?: string | null;
}

export interface EmailProposal {
  id: string;
  loadIndex: number;
  moduleVariant: string;
  preview: PreviewResponse & {
    sources?: string[];
    fieldSources?: Record<string, string>;
    suggestedCommit?: CommitRequestBody | null;
  };
  matches: ProposalMatches;
  issues: ProposalIssue[];
  conflicts: ProposalConflict[];
  duplicateOfLoadId: string | null;
  decision: string;
  decisionReasons: string[];
  createdLoadId: string | null;
  createdLoadDisplayId: string | null;
  createdQuoteId: string | null;
  createdQuoteDisplayId: string | null;
  /** Whether the created Quote row is a tender or a quote, and its module. */
  createdQuoteKind?: 'tender' | 'quote' | null;
  createdQuoteModule?: string | null;
  draft: CommitRequestBody | null;
  corrections: Record<string, { from: unknown; to: unknown }> | null;
  aiModel: string | null;
  promptVersion: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectReason: string | null;
  commitError: string | null;
}

export interface InboundEmailAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: string;
  skipReason: string | null;
  scanStatus: string;
  pageCount: number | null;
  downloadable: boolean;
}

export interface InboundEmailDetail {
  id: string;
  mailbox: {
    id: string;
    name: string;
    emailAddress: string;
    mode: MailboxMode;
    moduleVariant: string;
    outputType: MailboxOutputType;
  };
  fromAddress: string;
  fromName: string | null;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string | null;
  receivedAt: string;
  bodyText: string;
  bodyHtml: string;
  hasRaw: boolean;
  purgedAt: string | null;
  authResults: { spf: string; dkim: string; dmarc: string } | null;
  status: string;
  filterReason: string | null;
  failureReason: string | null;
  classification: string | null;
  classificationConfidence: number | null;
  classificationRationale: string | null;
  relatedLoad: CreatedRef | null;
  handledBy: string | null;
  handledAt: string | null;
  thread: Array<{
    id: string;
    subject: string | null;
    fromAddress: string;
    receivedAt: string;
    status: string;
  }>;
  aiModel: string | null;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  stageTimings: Record<string, number> | null;
  hasAiRawOutput: boolean;
  processedAt: string | null;
  attachments: InboundEmailAttachment[];
  proposals: EmailProposal[];
}

export interface MonitoringData {
  days: number;
  mailboxes: Array<{
    id: string;
    organizationId: string;
    organizationName: string | null;
    name: string;
    emailAddress: string;
    status: MailboxStatus;
    mode: MailboxMode;
    lastSyncAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    throttledUntil: string | null;
    consecutiveFailures: number;
    backlog: number;
  }>;
  funnel: {
    emails: Record<string, number>;
    proposals: Record<string, number>;
    reviewBacklog: number;
  };
  latencySeconds: { p50: number | null; p95: number | null; processed: number };
  /** Whether attachments are virus-scanned on this server. */
  scanner?: { configured: boolean; required: boolean };
  stageAvgMs: {
    classify: number | null;
    extract: number | null;
    decide: number | null;
    total: number | null;
  };
  ai: {
    inputTokens: number;
    outputTokens: number;
    estimatedCostUsd: number;
    budget: { monthlyBudgetUsd: number; monthSpendUsd: number } | null;
  };
  failures: Array<{ code: string; count: number }>;
  quality: { shadowWouldAutoCreate: number; approved: number; approvedWithCorrections: number };
}

export interface CommitResult {
  outputType: MailboxOutputType;
  loadId: string | null;
  quoteId: string | null;
  displayId: string | null;
  alreadyCreated: boolean;
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

const ROOT = ['email-intake'] as const;
export const emailIntakeKeys = {
  root: ROOT,
  mailboxes: [...ROOT, 'mailboxes'] as const,
  senderRules: [...ROOT, 'sender-rules'] as const,
  summary: [...ROOT, 'summary'] as const,
  oauthInfo: [...ROOT, 'oauth-info'] as const,
  monitoring: (scope: 'org' | 'all', days: number) => [...ROOT, 'monitoring', scope, days] as const,
  emails: (params: object) => [...ROOT, 'emails', params] as const,
  email: (id: string) => [...ROOT, 'email', id] as const,
};

// ---------------------------------------------------------------------------
// Mailboxes
// ---------------------------------------------------------------------------

export function useEmailMailboxes() {
  return useQuery({
    queryKey: emailIntakeKeys.mailboxes,
    queryFn: async () => (await api.get('/email-intake/mailboxes')).data.data as EmailMailbox[],
    staleTime: 30_000,
  });
}

export function useCreateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MailboxInput) =>
      (await api.post('/email-intake/mailboxes', input, { timeout: 90_000 })).data
        .data as EmailMailbox,
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useUpdateMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<MailboxInput> & { id: string; paused?: boolean }) =>
      (await api.patch(`/email-intake/mailboxes/${id}`, input, { timeout: 90_000 })).data
        .data as EmailMailbox,
    // Status / mode changes show in Monitoring and the queue too.
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useDeleteMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, purge }: { id: string; purge: boolean }) =>
      api.delete(`/email-intake/mailboxes/${id}?purge=${purge ? 'true' : 'false'}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useTestMailboxDraft() {
  return useMutation({
    mutationFn: async (input: MailboxInput & { mailboxId?: string | null }) =>
      (await api.post('/email-intake/mailboxes/test', input, { timeout: 90_000 })).data
        .data as ConnectionTestResult,
  });
}

export function useTestSavedMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/email-intake/mailboxes/${id}/test`, {}, { timeout: 90_000 })).data
        .data as ConnectionTestResult,
    onSuccess: () => qc.invalidateQueries({ queryKey: emailIntakeKeys.mailboxes }),
  });
}

export function useSyncMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.post(`/email-intake/mailboxes/${id}/sync`),
    onSuccess: () => {
      // The poll runs in the background — refresh status shortly after.
      setTimeout(() => qc.invalidateQueries({ queryKey: ROOT }), 5_000);
    },
  });
}

/** The redirect URI admins register on their OAuth app. */
export function useEmailIntakeOAuthInfo(enabled: boolean) {
  return useQuery({
    queryKey: emailIntakeKeys.oauthInfo,
    queryFn: async () =>
      (await api.get('/email-intake/oauth/info')).data.data as { redirectUri: string },
    enabled,
    staleTime: Infinity,
  });
}

/** FR-101/102 — start "Sign in with provider"; returns the consent URL to navigate to. */
export function useStartMailboxOAuth() {
  return useMutation({
    mutationFn: async (input: MailboxInput & { mailboxId?: string | null }) =>
      (await api.post('/email-intake/mailboxes/oauth/start', input)).data.data as {
        authorizeUrl: string;
        redirectUri: string;
      },
  });
}

/** "Finish sign-in" for a saved OAuth mailbox: the provider URL to send the browser to. */
export function useResumeMailboxOAuth() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/email-intake/mailboxes/${id}/oauth/resume`)).data.data as {
        authorizeUrl: string;
        redirectUri: string;
      },
  });
}

// ---------------------------------------------------------------------------
// Sender rules
// ---------------------------------------------------------------------------

export function useSenderRules() {
  return useQuery({
    queryKey: emailIntakeKeys.senderRules,
    queryFn: async () => (await api.get('/email-intake/sender-rules')).data.data as SenderRule[],
  });
}

export function useSaveSenderRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: SenderRuleInput & { id?: string }) =>
      id
        ? (await api.put(`/email-intake/sender-rules/${id}`, input)).data.data
        : (await api.post('/email-intake/sender-rules', input)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: emailIntakeKeys.senderRules }),
  });
}

export function useDeleteSenderRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/email-intake/sender-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: emailIntakeKeys.senderRules }),
  });
}

// ---------------------------------------------------------------------------
// Review queue
// ---------------------------------------------------------------------------

export function useEmailIntakeSummary() {
  return useQuery({
    queryKey: emailIntakeKeys.summary,
    queryFn: async () =>
      (await api.get('/email-intake/summary')).data.data as Partial<Record<EmailView, number>>,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export interface EmailListParams {
  view: EmailView;
  search?: string;
  mailboxId?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sortBy?: EmailSortField;
  sortDir?: 'asc' | 'desc';
}

/** Columns the server can sort the (paginated) queue by. */
export type EmailSortField = 'receivedAt' | 'fromAddress' | 'subject';

export function useInboundEmails(params: EmailListParams) {
  return useQuery({
    queryKey: emailIntakeKeys.emails(params),
    queryFn: async () => {
      const qs = new URLSearchParams({
        view: params.view,
        page: String(params.page ?? 1),
        limit: String(params.limit ?? 25),
        sortBy: params.sortBy ?? 'receivedAt',
        sortDir: params.sortDir ?? 'desc',
      });
      if (params.search) qs.set('search', params.search);
      if (params.mailboxId) qs.set('mailboxId', params.mailboxId);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      const res = await api.get(`/email-intake/emails?${qs.toString()}`);
      return res.data as {
        data: InboundEmailSummary[];
        pagination: { total: number; page: number; limit: number; totalPages: number };
      };
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
    // Keep the current page on screen while the next filter/page loads.
    placeholderData: keepPreviousData,
  });
}

const IN_FLIGHT = ['received', 'processing'];

export function useInboundEmail(id: string | undefined) {
  return useQuery({
    queryKey: emailIntakeKeys.email(id ?? ''),
    queryFn: async () =>
      (await api.get(`/email-intake/emails/${id}`)).data.data as InboundEmailDetail,
    enabled: !!id,
    staleTime: 0,
    // Keep polling while the AI pipeline is still working on this email:
    // every 4 s for the first minute, then every 15 s, then once a minute
    // (a stalled email is recovered server-side and then shows as failed).
    refetchInterval: (q) => {
      if (!q.state.data || !IN_FLIGHT.includes(q.state.data.status)) return false;
      const polls = q.state.dataUpdateCount;
      return polls < 15 ? 4_000 : polls < 50 ? 15_000 : 60_000;
    },
  });
}

export function useApproveProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, body }: { proposalId: string; body: CommitRequestBody }) =>
      (await api.post(`/email-intake/proposals/${proposalId}/approve`, body, { timeout: 120_000 }))
        .data.data as CommitResult,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: ['dispatch-loads'] });
      // The new load / tender must show in the loads lists and on Dispatch → Tenders.
      qc.invalidateQueries({ queryKey: ['loads'] });
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['locations'] });
    },
    // A refused create still changed the proposal (its error); show the current one.
    onError: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useRejectProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, reason }: { proposalId: string; reason: string | null }) =>
      api.post(`/email-intake/proposals/${proposalId}/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useMarkDuplicate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, loadNumber }: { proposalId: string; loadNumber: string }) =>
      (await api.post(`/email-intake/proposals/${proposalId}/duplicate`, { loadNumber })).data
        .data as {
        loadId: string;
        displayId: string | null;
      },
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      proposalId,
      draft,
    }: {
      proposalId: string;
      draft: CommitRequestBody | null;
    }) => api.put(`/email-intake/proposals/${proposalId}/draft`, { draft }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useReprocessEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: ReprocessStage }) =>
      api.post(`/email-intake/emails/${id}/reprocess`, { stage }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

/** The AI could not read the load: add a blank one to fill in by hand. */
export function useCreateManualProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emailId: string) =>
      (await api.post(`/email-intake/emails/${emailId}/manual-proposal`)).data.data as {
        proposalId: string;
      },
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useMarkHandled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.post(`/email-intake/emails/${id}/handled`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

// ---------------------------------------------------------------------------
// Monitoring (NFR-08)
// ---------------------------------------------------------------------------

export function useEmailIntakeMonitoring(scope: 'org' | 'all', days: number) {
  return useQuery({
    queryKey: emailIntakeKeys.monitoring(scope, days),
    queryFn: async () =>
      (
        await api.get(
          scope === 'all'
            ? `/email-intake/admin/monitoring?days=${days}`
            : `/email-intake/monitoring?days=${days}`
        )
      ).data.data as MonitoringData,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

async function fetchBlob(path: string): Promise<Blob> {
  const res = await api.get(path, { responseType: 'blob', timeout: 120_000 });
  return res.data as Blob;
}

/** Download the raw .eml or an attachment (always as a file, never rendered). */
export async function downloadIntakeFile(path: string, fileName: string): Promise<void> {
  const url = URL.createObjectURL(await fetchBlob(path));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** FR-802 — preview URL for a PDF / image attachment (typed by its sniffed MIME). */
export async function attachmentPreviewUrl(path: string, mimeType: string): Promise<string> {
  const blob = await fetchBlob(path);
  return URL.createObjectURL(new Blob([blob], { type: mimeType }));
}

/** Stable error code from an API error (client translates codes). */
export function apiErrorCode(err: unknown): string | null {
  const e = err as { response?: { data?: { error?: { code?: string } } } };
  return e?.response?.data?.error?.code ?? null;
}

export function apiErrorMessage(err: unknown): string | null {
  const e = err as { response?: { data?: { error?: { message?: string }; message?: string } } };
  return e?.response?.data?.error?.message ?? e?.response?.data?.message ?? null;
}
